import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, Package, TrendingUp, AlertTriangle, Activity, CreditCard } from "lucide-react";
import { api } from "@/lib/api";

const Card = ({ children, testid }) => (
  <div data-testid={testid} className="card-luxe p-6">{children}</div>
);
const Kpi = ({ label, value, icon: Icon, testid }) => (
  <Card testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</p>
        <p className="font-serif-luxe text-4xl gold-text mt-2">{value}</p>
      </div>
      <Icon size={28} strokeWidth={1.2} className="text-[#DCB457]/70" />
    </div>
  </Card>
);

export default function MyAdminDashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/myadmin/dashboard").then(r => setD(r.data)).catch(e => setErr(e?.response?.data?.detail || "Erreur"));
  }, []);
  if (err) return <p data-testid="myadmin-dashboard-error" className="text-red-300 text-sm">{err}</p>;
  if (!d) return <p className="text-white/55 italic">Chargement…</p>;
  return (
    <div data-testid="myadmin-dashboard">
      <header className="mb-8">
        <p className="eyebrow">MyAdmin</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Tableau de <span className="italic gold-text">bord.</span></h1>
      </header>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi label="Mariages actifs" value={d.kpi.active_events} icon={Calendar} testid="kpi-active-events" />
        <Kpi label="Clients MyEvent" value={d.kpi.total_clients} icon={Users} testid="kpi-clients" />
        <Kpi label="Forfaits actifs" value={d.kpi.active_plans} icon={Package} testid="kpi-plans" />
        <Kpi label="CA MyEvent" value={`${(d.kpi.revenue_eur ?? 0).toFixed(0)} €`} icon={TrendingUp} testid="kpi-revenue" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card testid="recent-events">
          <p className="eyebrow flex items-center gap-2"><Calendar size={11} /> Événements récents</p>
          {d.recent_events.length === 0 ? (
            <p className="text-white/50 text-sm mt-4 italic">Aucun événement pour le moment.</p>
          ) : (
            <ul className="mt-4 divide-y divide-[#DCB457]/10">
              {d.recent_events.map(e => (
                <li key={e.id} className="py-3 flex items-center justify-between">
                  <div>
                    <Link to={`/admin/events/${e.id}`} className="text-sm hover:text-[#DCB457]">{e.name}</Link>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{e.status} · {e.date || "sans date"}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card testid="attention">
          <p className="eyebrow flex items-center gap-2"><AlertTriangle size={11} className="text-amber-400" /> À traiter</p>
          {d.attention.length === 0 ? (
            <p className="text-white/50 text-sm mt-4 italic">Rien ne réclame votre attention.</p>
          ) : (
            <ul className="mt-4 divide-y divide-[#DCB457]/10">
              {d.attention.map(e => (
                <li key={e.id} className="py-3">
                  <Link to={`/admin/events/${e.id}`} className="text-sm hover:text-[#DCB457]">{e.name}</Link>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">{e.status}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card testid="recent-payments">
          <p className="eyebrow flex items-center gap-2"><CreditCard size={11} /> Paiements récents</p>
          {d.recent_payments.length === 0 ? <p className="text-white/50 text-sm mt-4 italic">Aucun paiement.</p> : (
            <ul className="mt-4 divide-y divide-[#DCB457]/10">
              {d.recent_payments.map(p => (
                <li key={p.id} className="py-3 flex items-center justify-between">
                  <span className="text-sm">{p.amount} €</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{p.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card testid="recent-activity">
          <p className="eyebrow flex items-center gap-2"><Activity size={11} /> Activité récente</p>
          {d.recent_activity.length === 0 ? <p className="text-white/50 text-sm mt-4 italic">Aucune activité.</p> : (
            <ul className="mt-4 divide-y divide-[#DCB457]/10 text-xs">
              {d.recent_activity.map(a => (
                <li key={a.id} className="py-2 flex justify-between gap-3">
                  <span className="text-white/70">{a.action}</span>
                  <span className="text-white/40 text-[10px]">{a.timestamp ? new Date(a.timestamp).toLocaleString("fr-FR") : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
